import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, merge, Subject } from 'rxjs';
import { ReviewsVotesSummaryComponent } from '../../_pages/main-page/_components/reviews/reviews-votes-summary/reviews-votes-summary.component';
import { SheetRef } from '../../_types/sheet-ref.type';
import { GuideStepDirective } from '../../guide/guide-step.directive';
import { GuideStep } from '../../guide/guide-step.enum';
import {
  ParkingPointActionsSheetData,
  ParkingPointActionsSheetResult,
} from './parking-point-actions-sheet.type';

@Component({
  selector: 'app-parking-point-actions-sheet',
  imports: [
    MatListModule,
    MatIconModule,
    MatButtonModule,
    ReviewsVotesSummaryComponent,
    MatTooltipModule,
    GuideStepDirective,
  ],
  templateUrl: './parking-point-actions-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkingPointActionsSheetComponent {
  private _sheetRef: MatBottomSheetRef = inject(MatBottomSheetRef);
  data: ParkingPointActionsSheetData = inject<ParkingPointActionsSheetData>(MAT_BOTTOM_SHEET_DATA);
  poiActionsEnum = ParkingPointActionsSheetResult;

  readonly totalReviews = this.data.parkingPoint.likeCount + this.data.parkingPoint.dislikeCount;

  readonly menuItems = [
    {
      label: 'Nawiguj',
      icon: 'navigation',
      result: ParkingPointActionsSheetResult.NAVIGATE,
      isPrimary: true,
    },
    {
      label: `Opinie (${this.totalReviews})`,
      icon: 'forum',
      result: ParkingPointActionsSheetResult.VIEW_REVIEWS,
    },
    {
      label: 'Popraw lokalizację',
      icon: 'edit_location_alt',
      result: ParkingPointActionsSheetResult.UPDATE_LOCATION,
    },
  ];

  sheetComponentRef: SheetRef<ParkingPointActionsSheetResult> = {
    dismiss: () => this._sheetRef.dismiss(),
    onDismiss: new Subject<ParkingPointActionsSheetResult>(),
  };

  constructor() {
    this.handleCancelSheet();
  }

  handleCancelSheet() {
    merge(
      this._sheetRef.backdropClick(),
      this._sheetRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')),
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._sheetRef.dismiss();
        this.sheetComponentRef.onDismiss.next(ParkingPointActionsSheetResult.DISMISS);
      });
  }

  getGuideStep(result: ParkingPointActionsSheetResult): GuideStep | null {
    const map: Partial<Record<ParkingPointActionsSheetResult, GuideStep>> = {
      [ParkingPointActionsSheetResult.NAVIGATE]: GuideStep.SHEET_NAVIGATE,
      [ParkingPointActionsSheetResult.VIEW_REVIEWS]: GuideStep.SHEET_REVIEWS,
    };
    return map[result] ?? null;
  }

  choose(result: ParkingPointActionsSheetResult): void {
    switch (result) {
      case ParkingPointActionsSheetResult.NAVIGATE:
        this._sheetRef.dismiss();
        this.sheetComponentRef.onDismiss.next(result);
        break;
      case ParkingPointActionsSheetResult.VIEW_REVIEWS:
        this._sheetRef.dismiss();
        this.sheetComponentRef.onDismiss.next(result);
        break;
      case ParkingPointActionsSheetResult.UPDATE_LOCATION:
        this.sheetComponentRef.onDismiss.next(result);
        break;
    }
  }

  ngOnDestroy() {
    this.sheetComponentRef.onDismiss.complete();
  }
}
